import {
  AddOnRateType,
  FuelSurchargeScheduleType,
  InvoiceLineItemType,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import * as yup from 'yup';

import { ResourceUsageLogType } from '~constants/enums';
import { AddOnCharge } from '~hooks/useAddOns/models';
import { ResourceUsageLog } from '~hooks/useResourceUsageLogs/models';
import { rateTypeTextByAddOnRateType } from '~pages/Settings/RatesManagement/AddOnCharges/addOnChargesDataGridColumnsDefinition';
import { toDecimal } from '~utils/decimal';
import { getEquipmentLabel } from '~utils/equipment';

/**
 * Utils
 */
const SEPARATOR = ' - ';
const RATE_FOR_EACH_DESCRIPTION = t('administration.rates.add_ons.rate_for_each');
const RATE_PERCENT_DESCRIPTION = t('administration.rates.add_ons.rate_percent_of_total');

export const appendAddOnRateTypeToName = (name: string, rateType: AddOnRateType) => {
  const rateTypeDescription = rateTypeTextByAddOnRateType[rateType];

  if (rateType === AddOnRateType.PER_LOAD || name.endsWith(rateTypeDescription)) {
    return `${name}`;
  }

  return `${name}${SEPARATOR}${rateTypeDescription}`;
};

export const removeAddOnRateTypeFromName = (name: string) => {
  if (name.endsWith(`${SEPARATOR}${RATE_FOR_EACH_DESCRIPTION}`)) {
    return name.slice(
      0,
      name.length - RATE_FOR_EACH_DESCRIPTION.length - SEPARATOR.length,
    );
  }

  if (name.endsWith(`${SEPARATOR}${RATE_PERCENT_DESCRIPTION}`)) {
    return name.slice(
      0,
      name.length - RATE_PERCENT_DESCRIPTION.length - SEPARATOR.length,
    );
  }

  return name;
};

/**
 * Schema helpers
 */
const dateSchema = yup
  .object()
  .required('')
  .test('is-valid-date', '', (val) => {
    const hasValue = Boolean(val);
    const isValid = dayjs.isDayjs(val) && val.isValid();

    return hasValue && isValid;
  });

const addOnChargeToAddOnChargeDTO = (addOnCharge: AddOnCharge) => {
  const dto: AddOnChargeDTO = {
    id: addOnCharge.id,
    addOnRateType: addOnCharge.revenueCostRateType,
    addOn: {
      id: addOnCharge.addOnId ?? '',
      name: addOnCharge.addOnId
        ? appendAddOnRateTypeToName(addOnCharge.name, addOnCharge.revenueCostRateType)
        : addOnCharge.name,
    },
    quantity: addOnCharge.quantity,
    value:
      addOnCharge.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL ||
      (addOnCharge.revenueCostRateType === AddOnRateType.FUEL_SURCHARGE &&
        !!addOnCharge.percentage)
        ? addOnCharge.percentage
        : (addOnCharge.rate ?? 0),
    rebillValue:
      addOnCharge.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL ||
      (addOnCharge.revenueCostRateType === AddOnRateType.FUEL_SURCHARGE &&
        !!addOnCharge.rebillPercentage)
        ? (addOnCharge.rebillPercentage ?? 0)
        : (addOnCharge.rebillRevenueRateValue ?? 0),
    load: addOnCharge.loadId ? { id: addOnCharge.loadId } : {},
    isPercentage:
      addOnCharge.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL ||
      (addOnCharge.revenueCostRateType === AddOnRateType.FUEL_SURCHARGE &&
        !!addOnCharge.percentage),
    jobId: addOnCharge.job?.jobId ?? null,
    surchargeType: addOnCharge.surchargeType,
  };

  return {
    ...dto,
    quantity: dto.quantity.toString(),
    value: dto.value.toString(),
    rebillValue: dto.rebillValue.toString(),
  } as unknown as AddOnChargeDTO;
};

const resourceUsageLogToResourceUsageLogDTO = (
  resourceUsageLog: ResourceUsageLog,
  type?: ResourceUsageLogType,
) => {
  const dto: ResourceUsageLogDTO = {
    id: resourceUsageLog.id ?? undefined,
    equipment: resourceUsageLog.equipment
      ? {
          id: resourceUsageLog.equipment?.id,
          name: getEquipmentLabel(resourceUsageLog.equipment),
        }
      : undefined,
    quantity: resourceUsageLog.usageMinutes / 60,
    phase: {
      id: resourceUsageLog.phase?.id,
      name: resourceUsageLog.phase?.name,
      code: resourceUsageLog.phase?.code,
    },
    driver: {
      id: resourceUsageLog.driver.id,
      name: resourceUsageLog.driver.fullName,
    },
    type: type,
  };

  return {
    ...dto,
    quantity: dto.quantity.toString(),
  } as unknown as ResourceUsageLogDTO;
};

export const schemaHelpers = {
  addOnChargeToAddOnChargeDTO,
  resourceUsageLogToResourceUsageLogDTO,
};
/**
 * Hourly rate
 */
export const hourlylLineItemSchema = yup.object().shape({
  id: yup.string().uuid().notRequired(),
  lineItemType: yup.string().notRequired(),
  billingAdjustmentMinutes: yup.number().integer('').required('').typeError(''),
  payStartTime: yup.mixed().when('lineItemType', {
    is: InvoiceLineItemType.MATERIAL_RATE_LINE_ITEM,
    then: () => yup.mixed().notRequired().nullable(),
    otherwise: () => dateSchema.clone(),
  }),
  payEndTime: yup.mixed().when('lineItemType', {
    is: InvoiceLineItemType.MATERIAL_RATE_LINE_ITEM,
    then: () => yup.mixed().notRequired().nullable(),
    otherwise: () => dateSchema.clone(),
  }),
  rate: yup.number().required(''),
  quantity: yup.number().min(0, '').required('').typeError(''),
});

type GenericNormalized<T> = Partial<T> & { id: string };

export type HourlyLineItemDTO = yup.InferType<typeof hourlylLineItemSchema>;
export type NormalizedHourlyLineItem = GenericNormalized<
  HourlyLineItemDTO & { quantity?: number }
>;

/**
 * Load rate
 */
export const loadlLineItemSchema = yup.object().shape({
  id: yup.string().uuid().notRequired(),
  quantity: yup.number().min(0, '').required('').typeError(''),
  rate: yup.number().required('').typeError(''),
});

export type LoadLineItemDTO = yup.InferType<typeof loadlLineItemSchema>;
export type NormalizedLoadLineItem = GenericNormalized<LoadLineItemDTO>;

/**
 * Ton rate
 */
export const tonlLineItemSchema = yup.object().shape({
  id: yup.string().uuid().notRequired(),
  quantity: yup.number().min(0, '').required('').typeError(''),
  rate: yup.number().required('').typeError(''),
});

export type TonLineItemDTO = yup.InferType<typeof tonlLineItemSchema>;
export type NormalizedTonLineItem = GenericNormalized<TonLineItemDTO>;

/**
 * Add-on charges
 */
export const addOnChargeSchema = yup.object().shape({
  id: yup.string().uuid().notRequired(),
  _destroy: yup.mixed().oneOf([1]).notRequired(),
  addOnRateType: yup
    .mixed()
    .oneOf([
      AddOnRateType.RATE_FOR_EACH,
      AddOnRateType.RATE_PERCENT_OF_TOTAL,
      AddOnRateType.PER_LOAD,
      AddOnRateType.FUEL_SURCHARGE,
    ])
    .required(),
  addOn: yup
    .object()
    .shape({
      id: yup.string().required(''),
      name: yup.string().required(''),
    })
    .required('')
    .nonNullable(),
  quantity: yup.number().required().typeError(''),
  // "value" will be either "rate" for rate-per-each based add-ons or "percentage" otherwise
  value: yup
    .number()
    .required('')
    .typeError('')
    .when(['addOnRateType'], ([addOnRateType], schema) => {
      if (addOnRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL) {
        return schema.min(-100, '').max(100, '');
      }

      return schema;
    }),
  // "rebillValue" will be the rebill rate value
  rebillValue: yup
    .number()
    .required('')
    .typeError('')
    .when(['addOnRateType'], ([addOnRateType], schema) => {
      if (addOnRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL) {
        return schema.min(-100, '').max(100, '');
      }

      return schema;
    }),
  load: yup.object().shape({
    id: yup.string().uuid().notRequired(),
  }),
  isPercentage: yup.boolean().notRequired(),
  jobId: yup.string().notRequired().nullable(),
  surchargeType: yup
    .mixed<FuelSurchargeScheduleType>()
    .oneOf(Object.values(FuelSurchargeScheduleType))
    .notRequired()
    .nullable(),
});

// oxlint-disable-next-line @typescript-eslint/no-unused-vars
const resourceAddOnChargeSchema = yup.object().shape({
  id: yup.string().uuid().notRequired(),
  type: yup.string(),
  equipment: yup
    .object()
    .shape({
      id: yup.string(),
      name: yup.string(),
    })
    // objects are non-optional in InferType unless explicitly widened; the
    // .when() below makes equipment genuinely optional for driver usage logs
    .optional()
    .default(undefined)
    .when('type', {
      is: ResourceUsageLogType.DRIVER,
      then: (schema) => {
        return schema.notRequired().nullable();
      },
      otherwise: (schema) => {
        return schema.required();
      },
    }),
  quantity: yup.number().required('').typeError(''),
  phase: yup.object().shape({
    id: yup.string(),
    name: yup.string(),
    code: yup.string(),
  }),
  driver: yup.object().shape({
    id: yup.string(),
    name: yup.string(),
  }),
  _destroy: yup.mixed().oneOf([1]).notRequired(),
});

export type AddOnChargeDTO = yup.InferType<typeof addOnChargeSchema>;

export type ResourceUsageLogDTO = yup.InferType<typeof resourceAddOnChargeSchema>;

/**
 * Invoice helpers
 */
// The line says whether it can be edited: a settled line is frozen while the invoice
// around it, and the other lines on it, are not. A line that does not say follows its
// invoice.
export const isLineEditable = (
  line: { editable: boolean | null },
  invoice: { editable: boolean },
) => (line.editable ?? invoice.editable) === true;

// These helpers must tolerate empty strings and nullish values because they
// render in the payables line-item editor while the user is still typing
// (React-Hook-Form keeps a cleared numeric field as ''). See TRE-14240.
export const roundToTwoDecimals = (value: number) =>
  toDecimal(value).toDecimalPlaces(2).toNumber();

export const decimalMultiply = (a: number, b: number) =>
  toDecimal(a).times(toDecimal(b)).toDecimalPlaces(2).toNumber();

const decimalPercentage = (total: number, percentage: number) =>
  toDecimal(total).times(toDecimal(percentage)).div(100).toDecimalPlaces(2).toNumber();

const calculateAddOnChargeTotal = (
  rateType: AddOnRateType | null,
  quantity: number = 0,
  rate: number = 0,
  percentage: number = 0,
  lineItemsSubtotal: number,
  freightTotal: number,
  freightQuantity?: number,
  surchargeType?: FuelSurchargeScheduleType | null,
) => {
  if (!rateType) {
    return 0;
  }

  if (rateType === AddOnRateType.RATE_FOR_EACH || rateType === AddOnRateType.PER_LOAD) {
    return decimalMultiply(quantity, rate);
  }

  if (rateType === AddOnRateType.FUEL_SURCHARGE) {
    if (percentage) {
      return decimalPercentage(freightTotal, percentage);
    }

    const chargesPerLoad = surchargeType === FuelSurchargeScheduleType.PER_LOAD;
    const effectiveQuantity = chargesPerLoad ? quantity : (freightQuantity ?? quantity);
    return decimalMultiply(effectiveQuantity, rate);
  }

  return decimalPercentage(freightTotal, percentage);
};

const calculateRebillAddOnChargeTotal = (
  rateType: AddOnRateType | null,
  quantity: number = 0,
  rebillRate: number = 0,
  percentage: number = 0,
  lineItemsSubtotal: number,
  freightTotal: number,
  freightQuantity?: number,
  surchargeType?: FuelSurchargeScheduleType | null,
) => {
  if (!rateType) {
    return 0;
  }

  if (rateType === AddOnRateType.RATE_FOR_EACH || rateType === AddOnRateType.PER_LOAD) {
    return decimalMultiply(quantity, rebillRate);
  }

  if (rateType === AddOnRateType.FUEL_SURCHARGE) {
    if (percentage) {
      return decimalPercentage(freightTotal, percentage);
    }

    const chargesPerLoad = surchargeType === FuelSurchargeScheduleType.PER_LOAD;
    const effectiveQuantity = chargesPerLoad ? quantity : (freightQuantity ?? quantity);
    return decimalMultiply(effectiveQuantity, rebillRate);
  }

  return decimalPercentage(freightTotal, percentage);
};

const calculateInvoiceTotal = (invoiceSubtotal: number, addOnsSum: number) => {
  return roundToTwoDecimals(invoiceSubtotal + addOnsSum);
};

// Returns true when the add-on's total depends on live line-item-derived
// values (lineItemsSubtotal, freightTotal, or freightQuantity) rather than
// only the add-on's own quantity/rate. Such totals must always be recomputed
// on the client to mirror what the backend would produce after save.
// Self-contained types (RATE_FOR_EACH, PER_LOAD) can safely fall
// back to the backend's stored totalAmount when the user has not edited them.
//
// Note: all FUEL_SURCHARGE variants depend on line items — the percentage
// variant reads freightTotal, the flat variant reads freightQuantity (see
// calculateAddOnChargeTotal), so both must recompute.
const addOnTotalDependsOnLineItems = (rateType: AddOnRateType | null | undefined) => {
  if (!rateType) return false;
  return (
    rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL ||
    rateType === AddOnRateType.FUEL_SURCHARGE
  );
};

const hasAddOnChargeEdits = (
  edited: AddOnChargeDTO | undefined,
  original: AddOnCharge | undefined,
  rebill: boolean = false,
): boolean => {
  if (!edited) return true;
  if (!original?.id) return true;
  if (edited._destroy === 1) return true;
  if (edited.addOnRateType !== original.revenueCostRateType) return true;

  if (Number(edited.quantity ?? 0) !== Number(original.quantity)) return true;

  // `edited.value` holds the percentage for percentage-based add-ons and the
  // rate otherwise (see addOnChargeToAddOnChargeDTO), so compare against the
  // matching field on `original` rather than always comparing against rate.
  const isPercentageBased =
    edited.addOnRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL ||
    (edited.addOnRateType === AddOnRateType.FUEL_SURCHARGE &&
      Boolean(edited.isPercentage));

  const editedValue = Number((rebill ? edited.rebillValue : edited.value) ?? 0);
  const originalValue = rebill
    ? Number(
        (isPercentageBased
          ? original.rebillPercentage
          : original.rebillRevenueRateValue) ?? 0,
      )
    : Number(isPercentageBased ? original.percentage : original.rate);
  return editedValue !== originalValue;
};

// Decides whether to recompute an add-on charge total client-side, or fall
// back to the backend's stored totalAmount. Recompute fires when:
// - the user edited the add-on itself (qty/value/rateType), OR
// - the add-on's total depends on line items AND a line item has actually
//   been edited.
//
// Without the line-item-edits gate, percentage/fuel-surcharge add-ons would
// always recompute via `freightTotal × pct` and drift by a cent from the
// backend's `Σ rounded(qty × rate)` subtotal — see TRE-14466.
const shouldRecomputeAddOnChargeTotal = (
  edited: AddOnChargeDTO | undefined,
  original: AddOnCharge | undefined,
  lineItemsHasEdits: boolean,
): boolean => {
  const dependsOnLineItems = addOnTotalDependsOnLineItems(
    edited?.addOnRateType as AddOnRateType | null | undefined,
  );
  return (
    (dependsOnLineItems && lineItemsHasEdits) || hasAddOnChargeEdits(edited, original)
  );
};

export const invoiceHelpers = {
  calculateAddOnChargeTotal,
  calculateRebillAddOnChargeTotal,
  calculateInvoiceTotal,
  addOnTotalDependsOnLineItems,
  hasAddOnChargeEdits,
  shouldRecomputeAddOnChargeTotal,
};

export const ShiftDetailsSchema = yup.object().shape({
  shiftTimeInHours: yup.number(),
  externalId: yup.string().nullable(),
  overrideReason: yup.string().nullable(),
  addOnCharges: yup.array().of(
    yup.object().shape({
      id: yup.string().uuid().notRequired(),
      _destroy: yup.mixed().oneOf([1]).notRequired(),
      addOnRateType: yup
        .mixed()
        .oneOf([
          AddOnRateType.RATE_FOR_EACH,
          AddOnRateType.RATE_PERCENT_OF_TOTAL,
          AddOnRateType.PER_LOAD,
          AddOnRateType.FUEL_SURCHARGE,
        ])
        .required(),
      addOn: yup
        .object()
        .shape({
          id: yup.string().required(''),
          name: yup.string().required(''),
        })
        .required(''),
      quantity: yup.number().required().typeError(''),
      // "value" will be either "rate" for rate-per-each based add-ons or "percentage" otherwise
      value: yup
        .number()
        .required('')
        .typeError('')
        .when(['addOnRateType'], ([addOnRateType], schema) => {
          if (addOnRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL) {
            return schema.min(-100, '').max(100, '');
          }

          return schema;
        }),
    }),
  ),
  resourceUsageLogs: yup.array().of(
    yup.object().shape({
      id: yup.string().uuid(),

      equipment: yup.object().shape({
        id: yup.string(),
        name: yup.string(),
      }),
      quantity: yup.number().required('').typeError(''),
      phase: yup
        .object()
        .shape({
          id: yup.string(),
          name: yup.string(),
          code: yup.string(),
        })
        .notRequired(),
      _destroy: yup.mixed().oneOf([1]).notRequired(),
    }),
  ),
  shiftStart: yup.date().nullable(),
  shiftEnd: yup.date().nullable(),
});

export const JobDetailsFormData = yup.object().shape({
  addOnCharges: yup.array().of(
    yup.object().shape({
      id: yup.string().uuid().notRequired(),
      _destroy: yup.mixed().oneOf([1]).notRequired(),
      addOnRateType: yup
        .mixed()
        .oneOf([
          AddOnRateType.RATE_FOR_EACH,
          AddOnRateType.RATE_PERCENT_OF_TOTAL,
          AddOnRateType.PER_LOAD,
          AddOnRateType.FUEL_SURCHARGE,
        ])
        .required(),
      addOn: yup
        .object()
        .shape({
          id: yup.string().required(''),
          name: yup.string().required(''),
        })
        .required(''),
      quantity: yup.number().required().typeError(''),
      // "value" will be either "rate" for rate-per-each based add-ons or "percentage" otherwise
      value: yup
        .number()
        .required('')
        .typeError('')
        .when(['addOnRateType'], ([addOnRateType], schema) => {
          if (addOnRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL) {
            return schema.min(-100, '').max(100, '');
          }

          return schema;
        }),
    }),
  ),
  resourceUsageLogs: yup.array().of(
    yup.object().shape({
      id: yup.string().uuid(),

      equipment: yup.object().shape({
        id: yup.string(),
        name: yup.string(),
      }),
      quantity: yup.number().required('').typeError(''),
      phase: yup
        .object()
        .shape({
          id: yup.string(),
          name: yup.string(),
          code: yup.string(),
        })
        .notRequired(),
      _destroy: yup.mixed().oneOf([1]).notRequired(),
    }),
  ),
});
